import React, { useState } from 'react';
import { FileText, Download, TrendingUp, Users, DollarSign, Home, Wrench, Building2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Reports = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [dateRange, setDateRange] = useState({
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
    });
    const [reportType, setReportType] = useState('financial');

    const generateReport = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/reports/${reportType}`, {
                params: dateRange
            });
            setReportData(response.data.data);
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportPDF = async () => {
        try {
            const response = await api.get('/reports/export/pdf', {
                params: { report_type: reportType, ...dateRange },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${reportType}_report_${Date.now()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting PDF:', error);
        }
    };

    const exportExcel = async () => {
        try {
            const response = await api.get('/reports/export/excel', {
                params: { report_type: reportType, ...dateRange },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${reportType}_report_${Date.now()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting Excel:', error);
        }
    };

    const getOccupancyColor = (rate) => {
        const r = parseFloat(rate);
        if (r >= 80) return 'text-green-600 bg-green-50';
        if (r >= 50) return 'text-yellow-600 bg-yellow-50';
        return 'text-red-600 bg-red-50';
    };

    const getStatusBadge = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-800',
            'available': 'bg-blue-100 text-blue-800',
            'rented': 'bg-purple-100 text-purple-800',
            'maintenance': 'bg-yellow-100 text-yellow-800',
            'pending': 'bg-yellow-100 text-yellow-800',
            'completed': 'bg-green-100 text-green-800',
            'failed': 'bg-red-100 text-red-800',
            'resolved': 'bg-green-100 text-green-800',
            'open': 'bg-yellow-100 text-yellow-800',
            'in_progress': 'bg-blue-100 text-blue-800',
            'cancelled': 'bg-gray-100 text-gray-800',
            'terminated': 'bg-red-100 text-red-800',
            'expired': 'bg-gray-100 text-gray-800',
            'draft': 'bg-gray-100 text-gray-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                <p className="text-gray-600">Generate and export detailed reports</p>
            </div>
            
            {/* Controls */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                        <select
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="financial">Financial Report</option>
                            <option value="occupancy">Occupancy Report</option>
                            <option value="maintenance">Maintenance Report</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={dateRange.start_date}
                            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                            type="date"
                            value={dateRange.end_date}
                            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div className="flex items-end space-x-3">
                        <button
                            onClick={generateReport}
                            disabled={loading}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            <FileText className="h-5 w-5 mr-2" />
                            Generate Report
                        </button>
                        {reportData && (
                            <>
                                <button
                                    onClick={exportPDF}
                                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    <Download className="h-5 w-5 mr-2" />
                                    PDF
                                </button>
                                <button
                                    onClick={exportExcel}
                                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    <Download className="h-5 w-5 mr-2" />
                                    Excel
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            
            {loading && <LoadingSpinner size="lg" />}
            
            {/* === FINANCIAL REPORT === */}
            {reportData && !loading && reportType === 'financial' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold mb-4">Financial Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-green-600 font-medium">Total Revenue</p>
                                    <DollarSign className="h-5 w-5 text-green-500" />
                                </div>
                                <p className="text-2xl font-bold text-green-700">
                                    KES {Number(reportData.total_revenue || 0).toLocaleString()}
                                </p>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-blue-600 font-medium">Total Transactions</p>
                                    <TrendingUp className="h-5 w-5 text-blue-500" />
                                </div>
                                <p className="text-2xl font-bold text-blue-700">{reportData.total_transactions || 0}</p>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-purple-600 font-medium">Average Transaction</p>
                                    <DollarSign className="h-5 w-5 text-purple-500" />
                                </div>
                                <p className="text-2xl font-bold text-purple-700">
                                    KES {Number(reportData.average_transaction || 0).toLocaleString()}
                                </p>
                            </div>
                            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-orange-600 font-medium">Pending Collection</p>
                                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                                </div>
                                <p className="text-2xl font-bold text-orange-700">
                                    KES {Number(reportData.pending_collection || 0).toLocaleString()}
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            Period: {dateRange.start_date} to {dateRange.end_date}
                        </p>
                    </div>

                    {/* Payments by Type */}
                    {reportData.payments_by_type && Object.keys(reportData.payments_by_type).length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">Payments by Type</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Payment Type</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Count</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Total (KES)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(reportData.payments_by_type).map(([type, data]) => (
                                            <tr key={type} className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="py-3 px-4 capitalize">{type.replace('_', ' ')}</td>
                                                <td className="py-3 px-4 text-right">{data.count}</td>
                                                <td className="py-3 px-4 text-right font-medium">{Number(data.total).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Monthly Breakdown */}
                    {reportData.monthly_breakdown && Object.keys(reportData.monthly_breakdown).length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">Monthly Breakdown</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Month</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Transactions</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Total (KES)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(reportData.monthly_breakdown).sort().reverse().map(([month, data]) => (
                                            <tr key={month} className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="py-3 px-4">{month}</td>
                                                <td className="py-3 px-4 text-right">{data.count}</td>
                                                <td className="py-3 px-4 text-right font-medium">{Number(data.total).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Recent Payments Table */}
                    {reportData.payments && reportData.payments.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">Recent Payments</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Type</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Amount (KES)</th>
                                            <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Reference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.payments.slice(0, 50).map((payment, idx) => (
                                            <tr key={payment.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="py-3 px-4">
                                                    {payment.transaction_date 
                                                        ? new Date(payment.transaction_date).toLocaleDateString() 
                                                        : new Date(payment.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-4 capitalize">{payment.payment_type?.replace('_', ' ')}</td>
                                                <td className="py-3 px-4 text-right font-medium">{Number(payment.amount).toLocaleString()}</td>
                                                <td className="py-3 px-4 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(payment.status)}`}>
                                                        {payment.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-gray-500">{payment.mpesa_receipt_number || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            {/* === OCCUPANCY REPORT === */}
            {reportData && !loading && reportType === 'occupancy' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold mb-4">Occupancy Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className={`p-4 rounded-lg border ${getOccupancyColor(reportData.overall_occupancy)}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium">Overall Occupancy</p>
                                    <Users className="h-5 w-5" />
                                </div>
                                <p className="text-2xl font-bold">{reportData.overall_occupancy}%</p>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-blue-600 font-medium">Total Assets</p>
                                    <Building2 className="h-5 w-5 text-blue-500" />
                                </div>
                                <p className="text-2xl font-bold text-blue-700">{reportData.total_assets || 0}</p>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-purple-600 font-medium">Total Units</p>
                                    <Home className="h-5 w-5 text-purple-500" />
                                </div>
                                <p className="text-2xl font-bold text-purple-700">{reportData.total_units || 0}</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-green-600 font-medium">Rented Units</p>
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                </div>
                                <p className="text-2xl font-bold text-green-700">{reportData.rented_units || 0}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-gray-600 font-medium">Available Units</p>
                                    <XCircle className="h-5 w-5 text-gray-500" />
                                </div>
                                <p className="text-2xl font-bold text-gray-700">{reportData.available_units || 0}</p>
                            </div>
                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-yellow-600 font-medium">Maintenance</p>
                                    <Wrench className="h-5 w-5 text-yellow-500" />
                                </div>
                                <p className="text-2xl font-bold text-yellow-700">{reportData.maintenance_units || 0}</p>
                            </div>
                            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-indigo-600 font-medium">Active Leases</p>
                                    <FileText className="h-5 w-5 text-indigo-500" />
                                </div>
                                <p className="text-2xl font-bold text-indigo-700">{reportData.active_leases || 0}</p>
                            </div>
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-red-600 font-medium">Vacant Units</p>
                                    <AlertTriangle className="h-5 w-5 text-red-500" />
                                </div>
                                <p className="text-2xl font-bold text-red-700">{reportData.vacant_units || 0}</p>
                            </div>
                        </div>
                    </div>

                    {/* Per-Asset Breakdown Table */}
                    {reportData.by_asset && reportData.by_asset.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">Assets Breakdown</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50">
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Asset Name</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Total Units</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Rented</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Available</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Maintenance</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Occupancy</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Base Rate (KES)</th>
                                            <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.by_asset.map((asset, idx) => {
                                            const occRate = parseFloat(asset.occupancy_rate) || 0;
                                            return (
                                                <tr key={asset.asset_id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                                    <td className="py-3 px-4 font-medium">{asset.name}</td>
                                                    <td className="py-3 px-4 capitalize">{asset.category}</td>
                                                    <td className="py-3 px-4 text-right">{asset.total_units}</td>
                                                    <td className="py-3 px-4 text-right text-green-600 font-medium">{asset.rented_units}</td>
                                                    <td className="py-3 px-4 text-right text-blue-600">{asset.available_units}</td>
                                                    <td className="py-3 px-4 text-right text-yellow-600">{asset.maintenance_units}</td>
                                                    <td className="py-3 px-4 text-right">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                            occRate >= 80 ? 'bg-green-100 text-green-700' :
                                                            occRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                                            'bg-red-100 text-red-700'
                                                        }`}>
                                                            {occRate}%
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-right">{Number(asset.base_rate).toLocaleString()}</td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(asset.status)}`}>
                                                            {asset.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                                            <td className="py-3 px-4">Total</td>
                                            <td className="py-3 px-4">{reportData.total_assets} assets</td>
                                            <td className="py-3 px-4 text-right">{reportData.total_units}</td>
                                            <td className="py-3 px-4 text-right text-green-600">{reportData.rented_units}</td>
                                            <td className="py-3 px-4 text-right text-blue-600">{reportData.available_units}</td>
                                            <td className="py-3 px-4 text-right text-yellow-600">{reportData.maintenance_units}</td>
                                            <td className="py-3 px-4 text-right">{reportData.overall_occupancy}%</td>
                                            <td className="py-3 px-4 text-right">—</td>
                                            <td className="py-3 px-4 text-center">—</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* No data state */}
                    {(!reportData.by_asset || reportData.by_asset.length === 0) && (
                        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                            <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-lg font-medium">No assets found</p>
                            <p className="text-sm">Create assets and units to see occupancy data.</p>
                        </div>
                    )}
                </div>
            )}
            
            {/* === MAINTENANCE REPORT === */}
            {reportData && !loading && reportType === 'maintenance' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold mb-4">Maintenance Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-yellow-600 font-medium">Open Tickets</p>
                                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                                </div>
                                <p className="text-2xl font-bold text-yellow-700">{reportData.summary?.open_tickets || 0}</p>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-blue-600 font-medium">In Progress</p>
                                    <Wrench className="h-5 w-5 text-blue-500" />
                                </div>
                                <p className="text-2xl font-bold text-blue-700">{reportData.summary?.in_progress_tickets || 0}</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-green-600 font-medium">Resolution Rate</p>
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                </div>
                                <p className="text-2xl font-bold text-green-700">{reportData.resolution_rate}%</p>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm text-purple-600 font-medium">Total Cost</p>
                                    <DollarSign className="h-5 w-5 text-purple-500" />
                                </div>
                                <p className="text-2xl font-bold text-purple-700">
                                    KES {Number(reportData.summary?.total_cost || 0).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Priority Breakdown */}
                    {reportData.by_priority && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold mb-4">By Priority</h3>
                                <div className="space-y-3">
                                    {Object.entries(reportData.by_priority).map(([priority, count]) => (
                                        <div key={priority} className="flex items-center justify-between">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                                                priority === 'emergency' ? 'bg-red-100 text-red-700' :
                                                priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                                priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-green-100 text-green-700'
                                            }`}>
                                                {priority}
                                            </span>
                                            <span className="font-semibold text-lg">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold mb-4">By Status</h3>
                                <div className="space-y-3">
                                    {Object.entries(reportData.by_status).filter(([_, count]) => count > 0).map(([status, count]) => (
                                        <div key={status} className="flex items-center justify-between">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(status)} capitalize`}>
                                                {status.replace('_', ' ')}
                                            </span>
                                            <span className="font-semibold text-lg">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tickets Table */}
                    {reportData.tickets && reportData.tickets.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">All Tickets</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50">
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">ID</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Title</th>
                                            <th className="text-center py-3 px-4 font-medium text-gray-600">Priority</th>
                                            <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Est. Cost (KES)</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-600">Actual Cost (KES)</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-600">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.tickets.map((ticket, idx) => (
                                            <tr key={ticket.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="py-3 px-4 text-gray-500">#{ticket.id}</td>
                                                <td className="py-3 px-4 font-medium">{ticket.title}</td>
                                                <td className="py-3 px-4 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                                        ticket.priority === 'emergency' ? 'bg-red-100 text-red-700' :
                                                        ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                                        ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-green-100 text-green-700'
                                                    }`}>
                                                        {ticket.priority}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)} capitalize`}>
                                                        {ticket.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-right">{Number(ticket.estimated_cost || 0).toLocaleString()}</td>
                                                <td className="py-3 px-4 text-right">{Number(ticket.actual_cost || 0).toLocaleString()}</td>
                                                <td className="py-3 px-4 text-gray-500">
                                                    {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* No data state */}
                    {(!reportData.tickets || reportData.tickets.length === 0) && (
                        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                            <Wrench className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-lg font-medium">No maintenance tickets</p>
                            <p className="text-sm">No maintenance tickets found for this period.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Reports;